import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

interface CookieSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: () => void;
}

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_PREFERENCES_KEY = 'aqarnet-cookie-preferences';

function getCookiePreferences(): CookiePreferences {
  try {
    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return {
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
  };
}

function saveCookiePreferences(prefs: CookiePreferences) {
  try {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore errors
  }
}

export function CookieSettingsDialog({ open, onOpenChange, onSave }: CookieSettingsDialogProps) {
  const { t } = useLanguage();
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences);

  useEffect(() => {
    if (open) {
      setPreferences(getCookiePreferences());
    }
  }, [open]);

  const handleSave = () => {
    saveCookiePreferences(preferences);
    toast.success(t('cookie_settings.saved'));
    onSave?.();
    onOpenChange(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    saveCookiePreferences(allAccepted);
    toast.success(t('cookie_settings.saved'));
    onSave?.();
    onOpenChange(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    saveCookiePreferences(onlyNecessary);
    toast.success(t('cookie_settings.saved'));
    onSave?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('cookie_settings.title')}</DialogTitle>
          <DialogDescription>
            {t('cookie_settings.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary Cookies */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="font-medium">{t('cookie_settings.necessary')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('cookie_settings.necessary_desc')}
              </p>
            </div>
            <Switch checked={true} disabled />
          </div>

          {/* Functional Cookies */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="font-medium">{t('cookie_settings.functional')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('cookie_settings.functional_desc')}
              </p>
            </div>
            <Switch
              checked={preferences.functional}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({ ...prev, functional: checked }))
              }
            />
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="font-medium">{t('cookie_settings.analytics')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('cookie_settings.analytics_desc')}
              </p>
            </div>
            <Switch
              checked={preferences.analytics}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({ ...prev, analytics: checked }))
              }
            />
          </div>

          {/* Marketing Cookies */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label className="font-medium">{t('cookie_settings.marketing')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('cookie_settings.marketing_desc')}
              </p>
            </div>
            <Switch
              checked={preferences.marketing}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({ ...prev, marketing: checked }))
              }
            />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleRejectAll} className="w-full sm:w-auto">
            {t('cookie_settings.reject_all')}
          </Button>
          <Button variant="outline" onClick={handleAcceptAll} className="w-full sm:w-auto">
            {t('cookie_settings.accept_all')}
          </Button>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            {t('cookie_settings.save_preferences')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
